import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { NotesPanel } from '@/components/notes/NotesPanel'
import { TaskComments } from '@/components/inbox/components/TaskComments'
import { refreshPlaylistCommentCounts } from '@/reducers/playlists/playlists.actions'

/**
 * Wrapper used by the playlist drawer and Call Explorer comments/task cells
 * to render the unified NotesPanel against a call. The `context` prop used
 * to switch between two parallel fetch/save paths (playlist vs. call
 * explorer) — both now hit the same /api/notes flow, so the
 * branch collapses to picking the org id off the right redux slice.
 *
 * `context === 'task'` routes to the inbox task drawer's notes.
 */
export const CommentEditorContainer = ({ callId, context = 'playlist', taskId, taskType }) => {
  const dispatch = useDispatch()
  const orgId = useSelector((state) => {
    // Only the playlist drawer reads org off the loaded playlist. The Call
    // Explorer surfaces (comments cell AND task notes cell) belong to the
    // callSearch results, so their org id lives on the callSearch filters —
    // `state.playlists` is empty there, which previously left task notes
    // fetching with organization_id=undefined.
    if (context === 'playlist') {
      return state.playlists?.playlist?.organization_id
    }
    return state.callSearch?.filters?.organizationId
  })

  if (context === 'task' && taskId) {
    return <TaskComments taskId={taskId} organizationId={orgId} taskType={taskType} />
  }

  if (!callId || !orgId) {
    return null
  }

  // From the playlist drawer, re-pull the per-call comment counts after a
  // mutation so the table's "View N Comments" badge stays in sync without a
  // full playlist reload. Other contexts have no derived count to refresh.
  const handleChange =
    context === 'playlist' ? () => dispatch(refreshPlaylistCommentCounts()) : undefined

  // This always renders inside a dedicated drawer (playlist comments / Call
  // Explorer comments), which the user already opened to comment — so show
  // the compose form directly rather than hiding it behind a second "Add a
  // note" trigger (collapsible only makes sense for the inline embedded
  // scorecard / criteria cards). Leave it non-compact so the panel keeps its
  // own 1rem padding — the drawer body has no horizontal padding of its own,
  // so a compact (padding: 0) panel would sit flush against the edges.
  return (
    <NotesPanel
      anchor={{ noteType: 'call', id: callId }}
      organizationId={orgId}
      collapsible={false}
      onChange={handleChange}
    />
  )
}
